import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ThumbsUp } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import { BlogHeader, BlogFooter } from "../components/blog/BlogHeader";
import { SignupCTA } from "../components/blog/SignupCTA";
import { CompareTable } from "../components/compare/CompareTable";
import { CHECKED, RIVALS, RIVAL_BY_SLUG } from "../data/compareData";

const useFaqSchema = (faq) => {
  useEffect(() => {
    if (!faq) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [faq]);
};

const List = ({ title, items, tone, testId }) => (
  <div className={`rounded-xl p-7 border ${tone === "dark" ? "bg-[#1A362D] text-[#F4F1EE] border-[#1A362D]" : "bg-white border-[#E5E1DB]"}`} data-testid={testId}>
    <h3 className="font-display text-xl">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed">
          {tone === "dark" ? <Check className="w-4 h-4 mt-1 shrink-0 text-[#D99C3D]" /> : <ThumbsUp className="w-4 h-4 mt-1 shrink-0 text-[#8B9D83]" />}
          <span className={tone === "dark" ? "text-[#F4F1EE]/90" : "text-[#5C6B64]"}>{i}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function CompareVs() {
  const { slug } = useParams();
  const r = RIVAL_BY_SLUG[slug];
  usePageMeta(r ? `${r.title} | Trackr` : "Compare | Trackr", r?.description);
  useFaqSchema(r?.faq);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!r) return <Navigate to="/compare" replace />;

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-5xl mx-auto px-6 pt-12">
        <Link to="/compare" className="inline-flex items-center gap-1 text-sm text-[#5C6B64] hover:text-[#C65D47] transition-colors" data-testid="compare-back-link">
          <ArrowLeft className="w-4 h-4" /> All comparisons
        </Link>
        <p className="mt-8 text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">Comparison</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] font-medium mt-3" data-testid="compare-vs-title">{r.title}</h1>
        <p className="text-base sm:text-lg text-[#5C6B64] mt-6 leading-relaxed max-w-3xl" data-testid="compare-vs-verdict">{r.verdict}</p>

        <section className="mt-12"><CompareTable only={["trackr", r.key]} /></section>

        <section className="grid md:grid-cols-2 gap-6 mt-12">
          <List title="Choose Trackr if you want…" items={r.trackrWins} tone="dark" testId="compare-trackr-wins" />
          <List title={`${r.name} might suit you if…`} items={r.rivalWins} testId="compare-rival-wins" />
        </section>

        <section className="mt-16" data-testid="compare-faq">
          <h2 className="font-display text-3xl tracking-tight mb-6">Frequently asked questions</h2>
          <div className="divide-y divide-[#E5E1DB] border-y border-[#E5E1DB]">
            {r.faq.map(([q, a]) => (
              <details key={q} className="group py-5">
                <summary className="cursor-pointer list-none flex justify-between items-center font-medium">
                  {q}<span className="text-[#C65D47] text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-sm text-[#5C6B64] mt-3 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <p className="text-xs text-[#5C6B64] mt-6">Details as of {CHECKED}, from {r.name}'s public pages. Check their site for current pricing. Trademarks belong to their owners.</p>
        <SignupCTA variant="end" />

        <section className="mt-16">
          <h2 className="font-display text-xl mb-4">More comparisons</h2>
          <div className="flex flex-wrap gap-3">
            {Object.values(RIVALS).filter((x) => x.slug !== slug).map((x) => (
              <Link key={x.slug} to={`/compare/${x.slug}`} className="rounded-full border border-[#E5E1DB] bg-white px-4 py-2 text-sm hover:border-[#1A362D] transition-colors" data-testid={`compare-more-${x.slug}`}>
                Trackr vs. {x.name}
              </Link>
            ))}
          </div>
        </section>
      </main>
      <BlogFooter />
    </div>
  );
}
